import { liveHours, type EmployerSnapshot } from '@klukka/client';
import {
  calcPeriod,
  daysFor,
  daysForRange,
  orlofPctFor,
  periodInfo,
  sumCalcs,
  unionRatesFor,
  type CompanySettings,
  type Employee,
  type PeriodCalc,
  type PeriodInfo,
  type Shift,
} from '@klukka/core';

export interface EmployeeCalc {
  emp: Employee;
  calc: PeriodCalc;
}

export interface Totals {
  gross: number;
  net: number;
  cost: number;
  pension: number;
  trygg: number;
  orlof: number;
  funds: number;
}

/** Hours a clocked-in employee has accumulated right now. */
function liveFor(snap: EmployerSnapshot, empId: string, offset: number): number {
  if (offset !== 0) return 0;
  const shift: Shift | undefined = snap.openShifts.find((s) => s.empId === empId);
  return shift ? liveHours(shift) : 0;
}

/** Every active employee's period calculation, live clocks folded in. */
export function calcAll(snap: EmployerSnapshot, offset: number): EmployeeCalc[] {
  const settings = snap.company.settings;
  const pinfo = periodInfo(settings, snap.serverToday, offset);
  return snap.employees
    .filter((e) => !e.inactive)
    .map((emp) => {
      const days = daysFor(emp, settings, snap.serverToday, liveFor(snap, emp.id, offset), offset);
      const adj = snap.adjustments
        .filter((a) => a.empId === emp.id && a.date >= pinfo.start && a.date <= pinfo.end)
        .reduce((s, a) => s + a.amount, 0);
      return { emp, calc: calcPeriod(emp, days, settings, adj) };
    });
}

export function totals(list: EmployeeCalc[]): Totals {
  return list.reduce<Totals>(
    (a, { calc }) => ({
      gross: a.gross + calc.gross,
      net: a.net + calc.net,
      cost: a.cost + calc.totalCost,
      pension: a.pension + calc.pensionEr,
      trygg: a.trygg + calc.trygg,
      orlof: a.orlof + calc.orlof,
      funds: a.funds + calc.funds,
    }),
    { gross: 0, net: 0, cost: 0, pension: 0, trygg: 0, orlof: 0, funds: 0 },
  );
}

export type PresenceKey = 'in' | 'sick' | 'vacation' | 'out' | 'none';

export interface Presence {
  emp: Employee;
  key: PresenceKey;
  color: string;
  since?: string;
  hours?: number;
}

/** Who is on the clock right now, who is out, who is off sick. */
export function presenceToday(snap: EmployerSnapshot): Presence[] {
  return snap.employees
    .filter((e) => !e.inactive)
    .map((emp) => {
      const shift = snap.openShifts.find((s) => s.empId === emp.id);
      if (shift) return { emp, key: 'in' as const, color: 'var(--live)', since: shift.clockInAt };
      const w = emp.worklog[snap.serverToday] ?? {};
      if (w.sick) return { emp, key: 'sick' as const, color: 'var(--warn)' };
      if (w.vacation) return { emp, key: 'vacation' as const, color: 'var(--ok)' };
      if ((w.h ?? 0) > 0) return { emp, key: 'out' as const, color: 'var(--primary)', hours: w.h };
      return { emp, key: 'none' as const, color: 'var(--ink3)' };
    });
}

export interface DayCost {
  date: string;
  weekend: boolean;
  holiday: boolean;
  isToday: boolean;
  gross: number;
  orlof: number;
  pension: number;
  funds: number;
  trygg: number;
  cost: number;
}

/** Employer cost broken out per calendar day of the period. */
export function dailyCost(snap: EmployerSnapshot, all: EmployeeCalc[], pinfo: PeriodInfo): DayCost[] {
  const settings = snap.company.settings;
  return pinfo.days
    .filter((d) => !d.future)
    .map((d) => {
      let gross = 0, orlof = 0, pension = 0, funds = 0, trygg = 0;
      for (const { emp, calc } of all) {
        const row = calc.rows.find((r) => r.date === d.date);
        if (!row?.gross) continue;
        // Priced at the rate the employee's seniority earned on that day.
        const o = (row.gross * orlofPctFor(emp, settings, row.date)) / 100;
        const p = (row.gross * (settings.pensionErPct + (emp.extraPensionPct ? settings.matchExtraPct : 0))) / 100;
        const f = (row.gross * unionRatesFor(emp, settings).employer) / 100;
        const tg = ((row.gross + o + p) * settings.tryggPct) / 100;
        gross += row.gross; orlof += o; pension += p; funds += f; trygg += tg;
      }
      return {
        date: d.date, weekend: d.weekend, holiday: d.holiday, isToday: d.isToday,
        gross, orlof, pension, funds, trygg,
        cost: gross + orlof + pension + funds + trygg,
      };
    });
}

export function sumDayCosts(days: DayCost[]): Omit<DayCost, 'date' | 'weekend' | 'holiday' | 'isToday'> {
  return days.reduce(
    (a, d) => ({
      gross: a.gross + d.gross, orlof: a.orlof + d.orlof, pension: a.pension + d.pension,
      funds: a.funds + d.funds, trygg: a.trygg + d.trygg, cost: a.cost + d.cost,
    }),
    { gross: 0, orlof: 0, pension: 0, funds: 0, trygg: 0, cost: 0 },
  );
}

/** Months of a year that have already started, clipped at today. */
export function monthsOfYear(year: number, today: string): Array<{ month: number; from: string; to: string }> {
  const out: Array<{ month: number; from: string; to: string }> = [];
  for (let m = 0; m < 12; m++) {
    const from = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    if (from > today) break;
    const last = new Date(year, m + 1, 0).getDate();
    let to = `${year}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
    if (to > today) to = today;
    out.push({ month: m, from, to });
  }
  return out;
}

/** One employee over an arbitrary range — used for YTD and the monthly matrix. */
export function calcRange(
  emp: Employee,
  settings: CompanySettings,
  from: string,
  to: string,
  periodDaysOverride?: number,
): PeriodCalc {
  return calcPeriod(emp, daysForRange(emp, settings, from, to, periodDaysOverride), settings);
}

export type AnalyticsScope = 'year' | 'period' | 'day';

/**
 * Analytics respects a cascade: scope (year / period / day) × employee filter.
 * A year is summed month by month so each month is taxed on its own proration
 * rather than one twelve-month lump.
 */
export function analyticsBase(
  snap: EmployerSnapshot,
  periodCalcs: EmployeeCalc[],
  scope: AnalyticsScope,
  year: number,
  day: string,
): EmployeeCalc[] {
  if (scope === 'period') return periodCalcs;
  const settings = snap.company.settings;
  const active = snap.employees.filter((e) => !e.inactive);

  if (scope === 'day') {
    const daysInMonth = new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)), 0).getDate();
    return active.map((emp) => ({ emp, calc: calcRange(emp, settings, day, day, daysInMonth) }));
  }

  const months = monthsOfYear(year, snap.serverToday);
  return active.map((emp) => ({
    emp,
    calc: sumCalcs(months.map(({ from, to }) => calcRange(emp, settings, from, to))),
  }));
}

/** Years that actually have recorded work — drives the tax-year and analytics pickers. */
export function recordedYears(snap: EmployerSnapshot): number[] {
  const years = new Set<number>();
  for (const e of snap.employees) {
    for (const d of Object.keys(e.worklog)) years.add(Number(d.slice(0, 4)));
  }
  years.add(Number(snap.serverToday.slice(0, 4)));
  return [...years].sort((a, b) => b - a);
}

export function hoursOf(calc: PeriodCalc): number {
  return calc.hours.dvH + calc.hours.yv1H + calc.hours.yv2H;
}
